(() => {
  // Ensure Swym callback queue
  window.SwymCallbacks = window.SwymCallbacks || [];

  const withSwym = (cb) => {
    if (window._swat && typeof window._swat.fetch === 'function') {
      cb(window._swat);
    } else {
      window.SwymCallbacks.push(cb);
    }
  };

  // ✅ ONLY target your header heart anchor(s)
  const headerIcons = () => document.querySelectorAll('a.wishlist-icon');

  const swymButtonSelector = '.swym-btn, .product-wishlist';
  const allSwymButtons = () => document.querySelectorAll(swymButtonSelector);

  // ✅ Toggle one class for styling (keep your added/not-added too if you want)
  const setHeaderState = (hasItems) => {
    headerIcons().forEach((el) => {
      el.classList.toggle('is-filled', hasItems);   // <-- use this in CSS
      el.classList.toggle('added', hasItems);
      el.classList.toggle('not-added', !hasItems);
    });
  };

  const fetchWishlist = (swat) =>
    new Promise((resolve) => {
      try {
        swat.fetch((items) => resolve(Array.isArray(items) ? items : []));
      } catch {
        resolve([]);
      }
    });

  let lastKnownHasItems = false;

  const updateHeaderState = (hasItems) => {
    lastKnownHasItems = hasItems;
    setHeaderState(hasItems);
  };

  const observeHeader = () => {
    const root = document.querySelector('#header-group') || document.body;
    const observer = new MutationObserver((mutations) => {
      if (!lastKnownHasItems) return;
      let needsReapply = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          needsReapply = true;
          break;
        }

        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target instanceof Element && target.matches('a.wishlist-icon') && !target.classList.contains('is-filled')) {
            needsReapply = true;
            break;
          }
        }
      }

      if (needsReapply) {
        setHeaderState(true);
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const bindSwymEvents = (swat) => {
    const layer = swat?.evtLayer || document;
    const events = swat?.JSEvents || {};
    const listen = (evt, handler) => {
      if (!evt || typeof evt !== 'string') return;
      layer.addEventListener(evt, handler);
    };

    const refresh = (opts) => updateButtonsFromWishlist(swat, opts);

    listen(events.addedToWishlist || 'sw:addedtowishlist', () => refresh({ forceRefresh: true }));
    listen(events.removedFromWishlist || 'sw:removedfromwishlist', () => {
      console.log('[swym] sw:removedfromwishlist fired');
      refresh({ forceRefresh: true });
    });
    listen(events.deletedFromRelay || 'sw:deletedfromrelay', () => refresh({ forceRefresh: true }));
    listen(events.removeWishlistCollection || 'sw:removeWishlistCollection', () => refresh({ forceRefresh: true }));
  };

  const normalizeUrlWithVariant = (btn) => {
    const productUrl = btn.dataset.productUrl;
    const variantId = btn.dataset.variantId;
    if (!productUrl || !variantId) return productUrl;
    try {
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('variant', variantId);
      return url.toString();
    } catch {
      return productUrl;
    }
  };

  const applyButtonState = (btn, added) => {
    btn.classList.toggle('swym-added', added);
    btn.classList.toggle('swym-removed', !added);
  };

  const syncButtonsFromList = (items) => {
    const epiSet = new Set(items.map((i) => String(i.epi)));
    allSwymButtons().forEach((btn) => {
      const variantId = btn.dataset.variantId;
      applyButtonState(btn, variantId && epiSet.has(String(variantId)));
    });
  };

  const handleToggle = async (swat, btn) => {
    const du = normalizeUrlWithVariant(btn);
    const payload = {
      epi: btn.dataset.variantId,
      du,
      empi: btn.dataset.productId,
      iu: btn.dataset.featuredImage,
      pr: btn.dataset.price,
      dt: btn.dataset.title,
    };

    const list = await fetchWishlist(swat);
    const existing = list.find(
      (item) => item.empi == payload.empi && (item.epi == payload.epi || item.epi == payload.empi)
    );

    if (existing) {
      swat.removeFromWishList(existing, async () => {
        applyButtonState(btn, false);
        const updated = await fetchWishlist(swat);
        updateHeaderState(updated.length > 0);
      });
      return;
    }

    swat.addToWishList(payload, async () => {
      applyButtonState(btn, true);
      const updated = await fetchWishlist(swat);
      updateHeaderState(updated.length > 0);
    });
  };

  const updateButtonsFromWishlist = async (swat, { forceRefresh = false } = {}) => {
    if (forceRefresh) {
      swat?.productEts?.makeCacheStale?.();
    }
    const list = await fetchWishlist(swat);
    syncButtonsFromList(list);
    updateHeaderState(list.length > 0);
  };

  const debounce = (fn, wait = 100) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const observeButtons = (swat) => {
    const refresh = debounce(() => updateButtonsFromWishlist(swat, { forceRefresh: true }), 150);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || !mutation.addedNodes.length) continue;
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(swymButtonSelector) || node.querySelector?.(swymButtonSelector)) {
            refresh();
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const initVariantListener = (swat) => {
    document.addEventListener('variantChange', () => updateButtonsFromWishlist(swat));
    document.addEventListener('variant:update', () => updateButtonsFromWishlist(swat));
    document.addEventListener('swym:collections-loaded', () =>
      updateButtonsFromWishlist(swat, { forceRefresh: true })
    );
  };

  const bindClicks = (swat) => {
    document.body.addEventListener('click', (evt) => {
      const target = evt.target.closest(swymButtonSelector);
      if (!target) return;
      evt.preventDefault();
      evt.stopPropagation();
      handleToggle(swat, target);
    });
  };

  withSwym((swat) => {
    updateButtonsFromWishlist(swat, { forceRefresh: true });
    initVariantListener(swat);
    bindClicks(swat);
    observeHeader();
    observeButtons(swat);
    bindSwymEvents(swat);
  });
})();
